import { FC, memo, useMemo, useState } from 'react';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import {
  CalculationErrorType,
  formatCodeName,
  getMapBy,
  Table,
  TextEllipsis,
  Tooltip,
  formatDateTimeShort,
  useSystemClassifierSelectOptions,
  getDayjs,
  translateProcessingError,
} from '@atlas-frontend/sdk';
import { useDescriptionFilter } from './useDescriptionFilter';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { path } from '../../../paths';
import { UiLink } from '../../../paths/UiLink';

import translations from './CalculationErrorsTable.translations.json';
import { ClippedContentCell } from './ClippedContentCell';
import { CalculationErrorDetailsModal } from './CalculationErrorDetailsModal';
import { useErrorReportDescriptionOptions } from './useErrorReportDescriptionOptions';

const { addTranslations, t } = i18nNsFactory(
  'output-data-processing-events-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<CalculationErrorType>();

export type CalculationErrorsTable = {
  data: CalculationErrorType[];
  scrollContainerSelector: string;
};

export const CalculationErrorsTable: FC<CalculationErrorsTable> = ({
  data,
  scrollContainerSelector,
}) => {
  const { PROCESSING_ERROR_TYPE: processingErrorTypes } =
    useSystemClassifierSelectOptions(
      ['PROCESSING_ERROR_TYPE'],
      ({ id, name }) => ({ id, name }),
    );

  const byId = useMemo(
    () => ({ errorTypeId: getMapBy(processingErrorTypes, 'id') }),
    [processingErrorTypes],
  );

  const [errorDetails, setErrorDetails] = useState<CalculationErrorType>();
  const { filters, setFilters } = useDescriptionFilter({
    data,
    filterId: 'dataObjectDescription',
    getDescriptionId: (error) => error.dataObjectDescriptionId,
    getFilterValue: (error) =>
      formatCodeName({
        code: error.dataObjectDescriptionCode,
        name: error.dataObjectDescriptionName,
      }),
  });

  const columns = useMemo(
    () => [
      columnHelper.accessor((rowData) => rowData.createdAt, {
        id: 'sequenceNumber',
        header: t('column.sequenceNumber'),
        cell: (props) => props.row.index + 1,
        enableColumnFilter: false,
        meta: {
          width: 100,
          className: 'sticky-left',
        },
      }),
      columnHelper.accessor(
        (rowData) =>
          formatCodeName({
            code: rowData.dataObjectDescriptionCode,
            name: rowData.dataObjectDescriptionName,
          }),
        {
          id: 'dataObjectDescription',
          header: t('column.dataObjectDescription'),
          filterFn: 'select',
        },
      ),
      columnHelper.accessor('algorithmCode', {
        header: t('column.algorithmCode'),
        cell: AlgorithmCodeCell,
      }),
      columnHelper.accessor('typeId', {
        header: t('column.typeId'),
        cell: (props) => (
          <ErrorTypeCell {...props} errorTypes={byId.errorTypeId} />
        ),
        filterFn: 'includesOption',
      }),
      columnHelper.accessor(translateProcessingError, {
        id: 'description',
        header: t('column.description'),
        enableSorting: false,
        cell: (props) => (
          <ClippedContentCell
            {...props}
            onShowMore={() => setErrorDetails(props.row.original)}
          />
        ),
        meta: { width: '30ch' },
      }),
    ],
    [byId.errorTypeId],
  );

  const descriptionOptions = useErrorReportDescriptionOptions(data);

  const options = useMemo(
    () => ({
      typeId: processingErrorTypes,
      dataObjectDescription: descriptionOptions,
    }),
    [processingErrorTypes, descriptionOptions],
  );

  return (
    <>
      <Table
        columns={columns}
        data={data}
        options={options}
        filters={filters}
        onFilter={setFilters}
        name="calculation-errors-table"
        sticky
        scrollContainerSelector={scrollContainerSelector}
      />
      {errorDetails && (
        <CalculationErrorDetailsModal
          error={errorDetails}
          onClose={() => setErrorDetails(undefined)}
        />
      )}
    </>
  );
};

const ErrorTypeCell: FC<
  CellContext<CalculationErrorType, any> & {
    errorTypes: Map<number, { id: number; name: string }>;
  }
> = memo((props) => {
  const { errorTypes } = props;
  const value = props.getValue();

  if (!value) {
    return null;
  }

  return <TextEllipsis>{errorTypes.get(value)?.name}</TextEllipsis>;
});

const AlgorithmCodeCell: FC<CellContext<CalculationErrorType, any>> = memo(
  (props) => {
    const {
      createdAt,
      dataObjectDescriptionVersionId,
      algorithmId,
      algorithmCode,
      algorithmModifiedAt,
    } = props.row.original;

    if (!algorithmId || !dataObjectDescriptionVersionId) {
      return <TextEllipsis>{algorithmCode}</TextEllipsis>;
    }

    const isAlgorithmUpdatedAfterError =
      createdAt &&
      algorithmModifiedAt &&
      getDayjs(createdAt)?.isBefore(getDayjs(algorithmModifiedAt));

    return (
      <TextEllipsis>
        <Tooltip
          title={
            isAlgorithmUpdatedAfterError &&
            t('tooltip', {
              modifiedAt: formatDateTimeShort(algorithmModifiedAt),
            })
          }
        >
          <TextEllipsis>
            <UiLink
              id={`output-description-${dataObjectDescriptionVersionId}-algorithm-${algorithmId}`}
              path={path.outputDataObjectView}
              params={{
                id: dataObjectDescriptionVersionId,
                segment: 'algorithms',
                algorithmId,
              }}
              target="_blank"
            >
              {algorithmCode}
            </UiLink>
          </TextEllipsis>
        </Tooltip>
      </TextEllipsis>
    );
  },
);
